// Library Methods
import axios from "axios";

// Utils
import {
  config,
  injectBearer,
  webServiceUrl,
} from "src/utils/services/servicesHelper";

// Types
import {
  ClinicExamStatsType,
  IOLLensType,
  ClinicPackageType,
  ClinicPatientStatsType,
  ClinicProgressionAnalysisStatsType,
  ClinicType,
  ClinicUserType,
  ProgressiveLensType,
  UpdateClinicSettingsType,
  AddEditProgressiveLensType,
  ClinicVideoType,
  DryEyeProfileType,
  AddEditDryEyeType,
} from "./types";
import { ExamType } from "../exams/types";

const getClinics = async (
  accessToken: string | null
): Promise<Array<ClinicType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getClinicById = async (
  accessToken: string | null,
  clinicId: string
): Promise<ClinicType> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const updateClinic = async (
  accessToken: string,
  clinicId: string,
  clinicData: ClinicType
): Promise<ClinicType> => {
  try {
    const { data } = await axios.put(
      `${webServiceUrl}/clinics/${clinicId}`,
      clinicData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const updateClinicSettings = async (
  accessToken: string,
  clinicId: string,
  clinicSettings: UpdateClinicSettingsType
): Promise<ClinicType["settings"]> => {
  try {
    const { data } = await axios.put(
      `${webServiceUrl}/clinics/${clinicId}/settings`,
      clinicSettings,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getClinicPackages = async (
  accessToken: string,
  clinicId: string
): Promise<Array<ClinicPackageType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/predefined-exams`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const addClinicPackage = async (
  accessToken: string,
  clinicId: string,
  packageData: { name: string; content: string }
) => {
  try {
    const { data } = await axios.post(
      `${webServiceUrl}/clinics/${clinicId}/predefined-exams`,
      packageData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const updateClinicPackage = async (
  accessToken: string,
  clinicId: string,
  packageId: string,
  packageData: { name: string; content: string }
) => {
  try {
    const { data } = await axios.put(
      `${webServiceUrl}/clinics/${clinicId}/predefined-exams/${packageId}`,
      packageData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const deleteClinicPackage = async (
  accessToken: string,
  clinicId: string,
  packageId: string
) => {
  try {
    const { data } = await axios.delete(
      `${webServiceUrl}/clinics/${clinicId}/predefined-exams/${packageId}`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getClinicExamStatistics = async (
  accessToken: string,
  clinicId: string,
  params: { recentFilterDate: string },
  signal: AbortSignal
): Promise<ClinicExamStatsType> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/dashboard/exam-statistics`,
      {
        headers: { Authorization: injectBearer(accessToken) },
        params,
        signal: signal,
      }
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getClinicPatientStatistics = async (
  accessToken: string,
  clinicId: string,
  params: { recentFilterDate: string },
  signal: AbortSignal
): Promise<ClinicPatientStatsType> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/dashboard/patient-statistics`,
      {
        headers: { Authorization: injectBearer(accessToken) },
        params,
        signal: signal,
      }
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getClinicProgressionAnalysisStatistics = async (
  accessToken: string,
  clinicId: string,
  params: { recentFilterDate: string },
  signal: AbortSignal
): Promise<ClinicProgressionAnalysisStatsType> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/dashboard/progression-analysis-statistics`,
      {
        headers: { Authorization: injectBearer(accessToken) },
        params,
        signal: signal,
      }
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getClinicUsers = async (
  accessToken: string,
  clinicId: string,
  signal: AbortSignal
): Promise<Array<ClinicUserType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/access`,
      config(accessToken, signal)
    );
    const formattedData: Array<ClinicUserType> = data?.data.map(
      (email: string) => ({ email })
    );
    return formattedData;
  } catch (error) {
    throw error;
  }
};

const addUser = async (
  accessToken: string,
  clinicId: string,
  userData: ClinicUserType
): Promise<ClinicUserType> => {
  try {
    const { data } = await axios.post(
      `${webServiceUrl}/clinics/${clinicId}/access`,
      userData,
      config(accessToken)
    );
    const formattedData: ClinicUserType = { email: data?.data };
    return formattedData;
  } catch (error) {
    throw error;
  }
};

const addAuth0User = async (
  accessToken: string,
  clinicId: string,
  userEmails: Array<string>
): Promise<void> => {
  try {
    await Promise.all(
      userEmails.map((userEmail) =>
        axios.post(
          `${webServiceUrl}/clinics/${clinicId}/access/${userEmail}/auth`,
          null,
          config(accessToken)
        )
      )
    );
  } catch (error) {
    throw error;
  }
};

const deleteUsers = async (
  accessToken: string,
  clinicId: string,
  userEmails: Array<string>
): Promise<void> => {
  try {
    await Promise.all(
      userEmails.map((userEmail) =>
        axios.delete(
          `${webServiceUrl}/clinics/${clinicId}/access/${userEmail}`,
          config(accessToken)
        )
      )
    );
  } catch (error) {
    throw error;
  }
};

const getSimulationEnvironmentTypes = async (
  accessToken: string
): Promise<Array<string>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/simulationEnvironmentTypes`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getIOLLensTypes = async (accessToken: string): Promise<Array<string>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/iolLensTypes`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getIOLLenses = async (
  accessToken: string,
  clinicId: string
): Promise<Array<IOLLensType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/iol-lenses`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const addIOLLens = async (
  accessToken: string,
  clinicId: string,
  lensData: { name: string; lensType: string; price: string }
): Promise<Array<IOLLensType>> => {
  try {
    const { data } = await axios.post(
      `${webServiceUrl}/clinics/${clinicId}/iol-lenses`,
      lensData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const updateIOLLens = async (
  accessToken: string,
  clinicId: string,
  lensData: IOLLensType
): Promise<Array<IOLLensType>> => {
  try {
    const { data } = await axios.put(
      `${webServiceUrl}/clinics/${clinicId}/iol-lenses/${lensData.id}`,
      lensData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const deleteIOLLens = async (
  accessToken: string,
  clinicId: string,
  lensId: string
): Promise<Array<IOLLensType>> => {
  try {
    const { data } = await axios.delete(
      `${webServiceUrl}/clinics/${clinicId}/iol-lenses/${lensId}`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getExamsForIOLLenses = async (
  accessToken: string,
  clinicId: string,
  lensId: string
): Promise<Array<ExamType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/iol-lenses/${lensId}/associated-exams`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getProgressiveIndexTypes = async (
  accessToken: string
): Promise<Array<string>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/indexTypes`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getProgressiveLensTypes = async (
  accessToken: string
): Promise<Array<string>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/progressiveLensTypes`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getProgressiveLenses = async (
  accessToken: string,
  clinicId: string
): Promise<Array<ProgressiveLensType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/progressive-lenses`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const addProgressiveLens = async (
  accessToken: string,
  clinicId: string,
  lensData: AddEditProgressiveLensType
): Promise<Array<ProgressiveLensType>> => {
  try {
    const { data } = await axios.post(
      `${webServiceUrl}/clinics/${clinicId}/progressive-lenses`,
      lensData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const updateProgressiveLens = async (
  accessToken: string,
  clinicId: string,
  lensData: ProgressiveLensType
): Promise<Array<ProgressiveLensType>> => {
  try {
    const { data } = await axios.put(
      `${webServiceUrl}/clinics/${clinicId}/progressive-lenses/${lensData.id}`,
      lensData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const deleteProgressiveLens = async (
  accessToken: string,
  clinicId: string,
  lensId: string
): Promise<Array<ProgressiveLensType>> => {
  try {
    const { data } = await axios.delete(
      `${webServiceUrl}/clinics/${clinicId}/progressive-lenses/${lensId}`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getExamsForProgressiveLenses = async (
  accessToken: string,
  clinicId: string,
  lensId: string
): Promise<Array<ExamType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/progressive-lenses/${lensId}/associated-exams`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getClinicVideos = async (
  accessToken: string,
  clinicId: string
): Promise<{ videos: Array<ClinicVideoType> }> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/videos`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const addClinicVideo = async (
  accessToken: string,
  clinicId: string,
  videoData: any
): Promise<Array<ClinicVideoType>> => {
  try {
    const { data } = await axios.post(
      `${webServiceUrl}/clinics/${clinicId}/videos`,
      videoData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const deleteClinicVideo = async (
  accessToken: string,
  clinicId: string,
  videoId: string
): Promise<Array<ClinicVideoType>> => {
  try {
    const { data } = await axios.delete(
      `${webServiceUrl}/clinics/${clinicId}/videos/${videoId}`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getExamsForVideos = async (
  accessToken: string,
  clinicId: string,
  videoId: string
): Promise<Array<ExamType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/videos/${videoId}/associated-exams`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getDryEyeProfiles = async (
  accessToken: string,
  clinicId: string
): Promise<Array<DryEyeProfileType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/dry-eye-profiles`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const addDryEyeProfile = async (
  accessToken: string,
  clinicId: string,
  profileData: AddEditDryEyeType
): Promise<Array<DryEyeProfileType>> => {
  try {
    const { data } = await axios.post(
      `${webServiceUrl}/clinics/${clinicId}/dry-eye-profiles`,
      profileData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const updateDryEyeProfile = async (
  accessToken: string,
  clinicId: string,
  profileData: DryEyeProfileType
): Promise<Array<DryEyeProfileType>> => {
  try {
    const { data } = await axios.put(
      `${webServiceUrl}/clinics/${clinicId}/dry-eye-profiles/${profileData.id}`,
      profileData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const deleteDryEyeProfile = async (
  accessToken: string,
  clinicId: string,
  profileId: string
): Promise<Array<ProgressiveLensType>> => {
  try {
    const { data } = await axios.delete(
      `${webServiceUrl}/clinics/${clinicId}/dry-eye-profiles/${profileId}`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getExamsForDryEyeProfiles = async (
  accessToken: string,
  clinicId: string,
  profileId: string
): Promise<Array<ExamType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/dry-eye-profiles/${profileId}/associated-exams`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getDryEyeDefaults = async (
  accessToken: string,
  clinicId: string
): Promise<Array<any>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/dry-eye-profiles/defaults`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

export {
  getClinics,
  getClinicById,
  updateClinic,
  updateClinicSettings,
  getClinicPackages,
  addClinicPackage,
  updateClinicPackage,
  deleteClinicPackage,
  getClinicExamStatistics,
  getClinicPatientStatistics,
  getClinicProgressionAnalysisStatistics,
  getClinicUsers,
  addAuth0User,
  addUser,
  deleteUsers,
  getSimulationEnvironmentTypes,
  getIOLLensTypes,
  getIOLLenses,
  addIOLLens,
  updateIOLLens,
  deleteIOLLens,
  getExamsForIOLLenses,
  getProgressiveIndexTypes,
  getProgressiveLensTypes,
  getProgressiveLenses,
  addProgressiveLens,
  updateProgressiveLens,
  deleteProgressiveLens,
  getExamsForProgressiveLenses,
  getClinicVideos,
  addClinicVideo,
  deleteClinicVideo,
  getExamsForVideos,
  getDryEyeProfiles,
  addDryEyeProfile,
  updateDryEyeProfile,
  deleteDryEyeProfile,
  getExamsForDryEyeProfiles,
  getDryEyeDefaults,
};
